import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { AlertTriangle, CheckCircle2, RefreshCw, Search, BarChart3 } from "lucide-react";
import { auditSeo } from "@/lib/seo-audit.functions";
import { getSeoPerformance, listSeoProperties, resubmitSitemap } from "@/lib/search-console.functions";
import { amIAdmin } from "@/lib/admin.functions";
import { useSession } from "@/lib/user-data";
import { breadcrumbSchema, webPageSchema } from "@/lib/schema";

export const Route = createFileRoute("/seo-monitor")({
  head: () => ({
    meta: [
      { title: "Pal Stars" },
      { name: "description", content: "Internal Pal Stars dashboard: on-page compliance audit and Google Search Console performance." },
      { property: "og:title", content: "Pal Stars" },
      { property: "og:description", content: "Internal Pal Stars monitoring dashboard." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://palstars.com/seo-monitor" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex, nofollow" },
    ],
    links: [{ rel: "canonical", href: "https://palstars.com/seo-monitor" }],
    scripts: [
      breadcrumbSchema([["Home", "/"], ["Pal Stars", "/seo-monitor"]]),
      webPageSchema({
        name: "Pal Stars",
        description: "Internal Pal Stars monitoring: on-page Search Essentials audit plus Google Search Console performance.",
        path: "/seo-monitor",
      }),
    ],
  }),
  component: SeoMonitorPage,
});

function pct(n: number) {
  return `${(n * 100).toFixed(1)}%`;
}

function OnPageAudit() {
  const run = useServerFn(auditSeo);
  const { data, isFetching, refetch } = useQuery({
    queryKey: ["seo-audit"],
    queryFn: () => run({}),
    staleTime: 60_000,
  });

  const failing = data?.reports.filter((r) => r.issues.length > 0) ?? [];
  const passing = (data?.reports.length ?? 0) - failing.length;

  return (
    <section className="glass rounded-xl p-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="flex items-center gap-2 text-lg font-semibold">
          <Search className="h-4 w-4 text-primary" /> On-page compliance audit
        </h2>
        <button
          onClick={() => refetch()}
          disabled={isFetching}
          className="inline-flex items-center gap-2 rounded-md border px-3 py-1.5 text-xs font-semibold hover:bg-card disabled:opacity-50"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${isFetching ? "animate-spin" : ""}`} /> Re-run audit
        </button>
      </div>
      <p className="aeo-answer mt-2 text-sm text-muted-foreground">
        Fetches every tracked page's rendered HTML and checks title length, meta description, self-referencing canonical,
        a single H1, Open Graph and Twitter tags, structured data, the Google Analytics tag and the legal disclaimer.
      </p>

      {isFetching && !data && <p className="mt-4 text-sm text-muted-foreground">Crawling routes…</p>}

      {data && (
        <>
          <div className="mt-4 grid gap-3 sm:grid-cols-3">
            <Stat label="Routes checked" value={String(data.reports.length)} />
            <Stat label="Passing" value={String(passing)} />
            <Stat label="With issues" value={String(failing.length)} tone={failing.length ? "warn" : "ok"} />
          </div>

          <div className="mt-5 overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="py-2 pr-3">Route</th>
                  <th className="py-2 pr-3">Title</th>
                  <th className="py-2 pr-3">Desc</th>
                  <th className="py-2 pr-3">H1</th>
                  <th className="py-2 pr-3">Schema</th>
                  <th className="py-2">Issues</th>
                </tr>
              </thead>
              <tbody>
                {data.reports.map((r) => (
                  <tr key={r.path} className="border-t align-top">
                    <td className="py-2 pr-3 font-mono text-xs">{r.path}</td>
                    <td className="py-2 pr-3 text-xs">{r.titleLength}</td>
                    <td className="py-2 pr-3 text-xs">{r.descriptionLength}</td>
                    <td className="py-2 pr-3 text-xs">{r.h1Count}</td>
                    <td className="py-2 pr-3 text-xs text-muted-foreground">{r.schemaTypes.join(", ") || "—"}</td>
                    <td className="py-2 text-xs">
                      {r.issues.length === 0 ? (
                        <span className="inline-flex items-center gap-1 text-primary"><CheckCircle2 className="h-3.5 w-3.5" /> Pass</span>
                      ) : (
                        <span className="inline-flex items-start gap-1 text-destructive">
                          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" /> {r.issues.join("; ")}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-3 text-xs text-muted-foreground">
            Last run {new Date(data.checkedAt).toLocaleString()} against {data.origin}
          </p>
        </>
      )}
    </section>
  );
}

function SearchConsolePanel() {
  const listProps = useServerFn(listSeoProperties);
  const perf = useServerFn(getSeoPerformance);
  const submit = useServerFn(resubmitSitemap);
  const [selected, setSelected] = useState<string | null>(null);
  const [days, setDays] = useState(28);

  const props = useQuery({ queryKey: ["gsc-properties"], queryFn: () => listProps({}), retry: false });
  const candidates = props.data?.properties ?? [];
  const siteUrl = selected ?? (candidates.length === 1 ? candidates[0] : null);

  const data = useQuery({
    queryKey: ["gsc-perf", siteUrl, days],
    queryFn: () => perf({ data: { siteUrl: siteUrl!, days } }),
    enabled: Boolean(siteUrl),
    retry: false,
  });

  const resubmit = useMutation({ mutationFn: () => submit({ data: { siteUrl: siteUrl! } }) });

  return (
    <section className="glass rounded-xl p-5">
      <h2 className="flex items-center gap-2 text-lg font-semibold">
        <BarChart3 className="h-4 w-4 text-primary" /> Google Search Console performance
      </h2>
      <p className="aeo-answer mt-2 text-sm text-muted-foreground">
        Live clicks, impressions, average position, top queries and top landing pages from the verified Search Console
        property for palstars.com, plus sitemap submission status.
      </p>

      {props.isError && (
        <p className="mt-4 text-sm text-destructive">{(props.error as Error).message}</p>
      )}
      {candidates.length > 1 && (
        <div className="mt-4 flex flex-wrap gap-2">
          {candidates.map((c) => (
            <button
              key={c}
              onClick={() => setSelected(c)}
              className={`rounded-md border px-3 py-1.5 text-xs font-semibold ${siteUrl === c ? "bg-card" : "hover:bg-card"}`}
            >{c}</button>
          ))}
        </div>
      )}
      {candidates.length > 1 && !selected && (
        <p className="mt-2 text-xs text-muted-foreground">Pick a property to load its performance data.</p>
      )}

      {siteUrl && (
        <>
          <div className="mt-4 flex flex-wrap items-center gap-2">
            {[7, 28, 90].map((d) => (
              <button
                key={d}
                onClick={() => setDays(d)}
                className={`rounded-md border px-3 py-1.5 text-xs font-semibold ${days === d ? "bg-card" : "hover:bg-card"}`}
              >Last {d} days</button>
            ))}
            <button
              onClick={() => resubmit.mutate()}
              disabled={resubmit.isPending}
              className="ml-auto rounded-md border px-3 py-1.5 text-xs font-semibold hover:bg-card disabled:opacity-50"
            >{resubmit.isPending ? "Submitting…" : "Re-submit sitemap"}</button>
          </div>
          {resubmit.isSuccess && <p className="mt-2 text-xs text-primary">Sitemap re-submitted.</p>}
          {resubmit.isError && <p className="mt-2 text-xs text-destructive">{(resubmit.error as Error).message}</p>}

          {data.isLoading && <p className="mt-4 text-sm text-muted-foreground">Loading Search Console data…</p>}
          {data.isError && <p className="mt-4 text-sm text-destructive">{(data.error as Error).message}</p>}

          {data.data && (
            <>
              <div className="mt-4 grid gap-3 sm:grid-cols-4">
                <Stat label="Clicks" value={data.data.totals.clicks.toLocaleString()} />
                <Stat label="Impressions" value={data.data.totals.impressions.toLocaleString()} />
                <Stat label="CTR" value={pct(data.data.totals.ctr)} />
                <Stat label="Avg position" value={data.data.totals.position.toFixed(1)} />
              </div>
              <p className="mt-2 text-xs text-muted-foreground">
                {data.data.range.start} → {data.data.range.end} · {data.data.siteUrl}
                {data.data.sitemap?.lastDownloaded
                  ? ` · sitemap last read ${new Date(data.data.sitemap.lastDownloaded).toLocaleDateString()}`
                  : ""}
              </p>

              <div className="mt-5 grid gap-6 lg:grid-cols-2">
                <RowTable title="Top queries" rows={data.data.queries} />
                <RowTable title="Top pages" rows={data.data.pages} />
              </div>
            </>
          )}
        </>
      )}
    </section>
  );
}

function RowTable({ title, rows }: { title: string; rows: Array<{ key: string; clicks: number; impressions: number; ctr: number; position: number }> }) {
  return (
    <div>
      <h3 className="mb-2 text-sm font-semibold">{title}</h3>
      {rows.length === 0 ? (
        <p className="text-xs text-muted-foreground">No data in this range yet.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead className="uppercase tracking-wide text-muted-foreground">
              <tr><th className="py-1.5 pr-2">Key</th><th className="py-1.5 pr-2">Clicks</th><th className="py-1.5 pr-2">Impr.</th><th className="py-1.5 pr-2">CTR</th><th className="py-1.5">Pos</th></tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.key} className="border-t">
                  <td className="py-1.5 pr-2 max-w-[16rem] truncate" title={r.key}>{r.key}</td>
                  <td className="py-1.5 pr-2">{r.clicks}</td>
                  <td className="py-1.5 pr-2">{r.impressions}</td>
                  <td className="py-1.5 pr-2">{pct(r.ctr)}</td>
                  <td className="py-1.5">{r.position.toFixed(1)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, tone }: { label: string; value: string; tone?: "ok" | "warn" }) {
  return (
    <div className="rounded-lg border p-3">
      <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className={`mt-1 text-xl font-bold ${tone === "warn" ? "text-destructive" : tone === "ok" ? "text-primary" : ""}`}>{value}</div>
    </div>
  );
}

function SeoMonitorPage() {
  const { session } = useSession();
  const check = useServerFn(amIAdmin);
  const admin = useQuery({ queryKey: ["am-i-admin"], queryFn: () => check({}), enabled: Boolean(session), retry: false });
  const isAdmin = admin.data?.admin === true;

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 py-10 space-y-8">
      <header>
        <p className="text-xs uppercase tracking-widest text-primary">Internal tooling</p>
        <h1 className="mt-2 text-3xl sm:text-4xl font-bold">Pal Stars</h1>
        <p className="mt-3 max-w-2xl text-muted-foreground">
          Admin-only monitoring for Pal Stars: an on-page Search Essentials audit that runs on demand, and live
          Google Search Console performance for the verified palstars.com property.
        </p>
        <Link
          to="/seo-audit"
          className="mt-3 inline-flex items-center gap-2 rounded-md border px-3 py-2 text-xs font-semibold hover:bg-card"
        >
          Open SEO audit tools (canonical preview, schema validator, robots, sitemap refresh, URL inspection)
        </Link>
      </header>

      {isAdmin ? (
        <>
          <OnPageAudit />
          <SearchConsolePanel />
        </>
      ) : (
        <section className="glass rounded-xl p-5">
          <h2 className="text-lg font-semibold">Admins only</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            {!session
              ? "Sign in with a Pal Stars admin account to view this dashboard."
              : admin.isLoading
                ? "Checking your access…"
                : "This dashboard is restricted to Pal Stars admins."}
          </p>
        </section>
      )}
    </div>
  );
}
